import { supabase } from '../lib/supabase';
import { CreditBalance, MonthlyUsage } from '../types/credits';

/**
 * Get the credit balance for a user.
 * Falls back to a default balance if the credits table doesn't exist yet.
 */
export async function getCreditBalance(
  userId: string
): Promise<{ success: boolean; data?: CreditBalance; error?: string }> {
  try {
    // Cast to any because the credits table may not exist in DB types yet
    const { data, error } = await (supabase.client.from as any)('credits')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      // Table may not exist yet — return placeholder
      console.warn('Credits table not available:', error.message);
      return { success: true, data: getPlaceholderBalance() };
    }

    if (!data || data.length === 0) {
      return { success: true, data: getPlaceholderBalance() };
    }

    // Aggregate across all credit records for this user
    let total = 0;
    let used = 0;
    let rolloverCredits = 0;
    let rolloverExpiresAt: string | null = null;
    let subscriptionCredits = 0;
    let singlePurchaseCredits = 0;

    for (const record of data as any[]) {
      total += record.total_credits || 0;
      used += record.used_credits || 0;

      if (record.credit_type === 'rollover') {
        rolloverCredits += (record.total_credits || 0) - (record.used_credits || 0);
        rolloverExpiresAt = record.expires_at;
      } else if (record.credit_type === 'subscription') {
        subscriptionCredits += (record.total_credits || 0) - (record.used_credits || 0);
      } else if (record.credit_type === 'single_purchase') {
        singlePurchaseCredits += (record.total_credits || 0) - (record.used_credits || 0);
      }
    }

    return {
      success: true,
      data: {
        total,
        used,
        remaining: total - used,
        rolloverCredits,
        rolloverExpiresAt,
        subscriptionCredits,
        singlePurchaseCredits,
      },
    };
  } catch (err) {
    console.error('Error fetching credit balance:', err);
    return { success: true, data: getPlaceholderBalance() };
  }
}

/**
 * Get monthly usage history for the credits usage chart.
 */
export async function getCreditUsageHistory(
  userId: string,
  months: number = 6
): Promise<{ success: boolean; data?: MonthlyUsage[]; error?: string }> {
  try {
    // Cast to any because the credit_transactions table may not exist in DB types yet
    const { data, error } = await (supabase.client.from as any)('credit_transactions')
      .select('created_at, amount')
      .eq('user_id', userId)
      .eq('transaction_type', 'debit')
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('Credit transactions table not available:', error.message);
      return { success: true, data: getPlaceholderUsage(months) };
    }

    // Group by month
    const monthlyMap = new Map<string, number>();
    const now = new Date();

    for (let i = 0; i < months; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthlyMap.set(key, 0);
    }

    if (data) {
      for (const tx of data as any[]) {
        const d = new Date(tx.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (monthlyMap.has(key)) {
          monthlyMap.set(key, (monthlyMap.get(key) || 0) + Math.abs(tx.amount));
        }
      }
    }

    const result: MonthlyUsage[] = Array.from(monthlyMap.entries())
      .map(([month, count]) => ({ month, count }))
      .reverse();

    return { success: true, data: result };
  } catch (err) {
    console.error('Error fetching usage history:', err);
    return { success: true, data: getPlaceholderUsage(months) };
  }
}

function getPlaceholderBalance(): CreditBalance {
  return {
    total: 0,
    used: 0,
    remaining: 0,
    rolloverCredits: 0,
    rolloverExpiresAt: null,
    subscriptionCredits: 0,
    singlePurchaseCredits: 0,
  };
}

function getPlaceholderUsage(months: number): MonthlyUsage[] {
  const result: MonthlyUsage[] = [];
  const now = new Date();
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    result.push({
      month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      count: 0,
    });
  }
  return result;
}
